'use client';
import { ColumnsType } from 'antd/lib/table';
import React, { useState, useEffect } from 'react';
import { Button } from '@benzinga/core-ui';
import { QuoteProtos, ScannerProtos } from '@benzinga/scanner-manager';
import { SimpleScanner, Preset, SimpleFilters, Format, formatLongHumanReadable } from './SimpleScanner';
import styled from '@benzinga/themetron';
import { Ticker } from '@benzinga/ticker-ui';
import { DateTime } from 'luxon';
import { configurePreset, presets as initialPresets } from './presets';

/*
const priceOptions: Option[] = [
    {
        name: 'Any',
        value: '',
    },
    {
        name: '> 100',
        value: 'bt 100,',
    },
    {
        name: '> 10',
        value: 'bt 10,',
    },
    {
        name: '> 5',
        value: 'bt 5,',
    },
    {
        name: '> 1',
        value: 'bt 1,',
    },
    {
        name: '< 1',
        value: 'bt 0,1',
    },
    {
        name: '< 5',
        value: 'bt 0,5',
    },
    {
        name: '< 10',
        value: 'bt 0,10',
    },
];

const marketCapOptions: Option[] = [
    {
        name: 'Any',
        value: '',
    },
    {
        name: '> 500b',
        value: 'bt 500000000000,',
    },
    {
        name: '> 100b',
        value: 'bt 100000000000,',
    },
    {
        name: '> 10b',
        value: 'bt 10000000000,',
    },
    {
        name: '> 1b',
        value: 'bt 1000000000,',
    },
    {
        name: '< 10b',
        value: 'bt 10000000000,',
    },
];

const changePercentOptions: Option[] = [
    {
        name: 'Any',
        value: '',
    },
    {
        name: '> 10%',
        value: 'bt 10,',
    },
    {
        name: '> 5%',
        value: 'bt 5,',
    },
    {
        name: '> 2%',
        value: 'bt 2,',
    },
];
*/

const columns: ColumnsType<QuoteProtos.IQuote> = [
  {
    dataIndex: 'symbol',
    key: 'symbol',
    render: value => {
      const link = `https://www.benzinga.com/quote/${value}`;

      return (
        <div className="flex items-center justify-between">
          <Ticker
            symbol={value}
            targetElement={
              <a href={link} rel="noreferrer" target="_blank">
                {value}
              </a>
            }
          />
          <div className="buy-btn-wrapper">
            <Button
              as="a"
              data-label="Buy Now"
              href="https://www.benzinga.com/go/buy-now-analyst"
              key={value}
              rel="noopener noreferrer"
              target="_blank"
              variant="primary"
            >
              Buy Now
            </Button>
          </div>
        </div>
      );
    },
    title: 'Symbol',
    width: 150,
  },
  // {
  //   dataIndex: 'price',
  //   key: 'price',
  //   render: Format.price,
  //   sortDirections: [],
  //   sorter: true,
  //   title: 'Price Change',
  //   width: 100
  // },
  // {
  //   dataIndex: 'changePercent',
  //   key: 'changePercent',
  //   render: (value) => {
  //     const changeValue = Format.percentage(value);
  //     return Format.colorfulChange(value, changeValue);
  //   },
  //   sortDirections: [],
  //   sorter: true,
  //   title: 'Price % Change',
  //   width: 115
  // },
  {
    dataIndex: 'sharesShortPercentOfFloat',
    key: 'sharesShortPercentOfFloat',
    render: Format.percentage,
    sortDirections: [],
    sorter: true,
    title: 'Current Short Int.',
    width: 130,
  },
  {
    dataIndex: 'sharesShortPreviousPercentOfFloat',
    key: 'sharesShortPreviousPercentOfFloat',
    render: Format.percentage,
    sortDirections: [],
    sorter: true,
    title: 'Previous Short Int.',
    width: 135,
  },
  {
    dataIndex: 'sharesShortChange',
    key: 'sharesShortChange',
    render: Format.number,
    sortDirections: [],
    sorter: true,
    title: 'Short Int. Change',
    width: 130,
  },
  {
    dataIndex: 'sharesShortChangePercent',
    key: 'sharesShortChangePercent',
    render: Format.percentage,
    sortDirections: [],
    sorter: true,
    title: 'Short Int. % Change',
    width: 145,
  },
  {
    dataIndex: 'shortInterestRatio',
    key: 'shortInterestRatio',
    render: (value: number) => value?.toFixed(1),
    sortDirections: [],
    sorter: true,
    title: 'Days to Cover',
    width: 110,
  },
  {
    dataIndex: 'sharesShortValue',
    key: 'sharesShortValue',
    render: formatLongHumanReadable,
    sortDirections: [],
    sorter: true,
    title: 'Shares Short Value',
    width: 140,
  },
  {
    dataIndex: 'averageVolume',
    key: 'averageVolume',
    render: formatLongHumanReadable,
    sortDirections: [],
    sorter: true,
    title: 'Avg Daily Volume',
    width: 135,
  },
  {
    dataIndex: 'marketCap',
    key: 'marketCap',
    render: formatLongHumanReadable,
    sortDirections: [],
    sorter: true,
    title: 'Market Cap',
    width: 90,
  },
];

/**
 * A list of control that will appear, in order, at the top of the table.
 */
/*
const filterControls: FilterControl[] = [
    {
        fieldName: 'price',
        label: 'Price',
        placeholder: '[price filter]',
        options: priceOptions,
    },
    {
        fieldName: 'marketCap',
        label: 'Market Cap',
        options: marketCapOptions,
    },
    {
        fieldName: 'sharesShortPercentOfFloat',
        label: 'Short Interest',
        options: changePercentOptions,
    },
    {
        fieldName: 'sharesShort',
        label: 'Shares Short',
        options: marketCapOptions,
    },
    {
        fieldName: 'sharesShortChangePercent',
        label: 'Short Change %',
        options: changePercentOptions,
    },
];
*/

// function parseSearchParams() {
//   const searchParams = new URLSearchParams(window.location.search);
//   const filters: SimpleFilters = {};
//   searchParams.getAll('filter').forEach(filterParam => {
//     const space = filterParam.indexOf(' ');
//     filters[filterParam.substr(0, space)] = filterParam.substr(space + 1);
//   });
//   return filters;
// }

interface ShortInterestScannerProps {
  preset?: PresetType;
  limit?: number;
  onPresetChange?: (query: ScannerProtos.Query, presetName: PresetType) => void;
  initialScannerResult?: ScannerProtos.IQueryResponse | null;
}

export type PresetType = 'largest-increase' | 'largest-decrease' | 'most-shorted';

export const ShortInterestScanner: React.FC<ShortInterestScannerProps> = props => {
  const [limit, setLimit] = useState<number>(props.limit || 100);
  const [active, setActive] = useState<PresetType>(props.preset ?? 'most-shorted');

  useEffect(() => {
    if (props.limit) {
      setLimit(props.limit);
    }
  }, [props.limit]);

  const presets = initialPresets.map(preset => configurePreset(preset, { limit }));

  const getPresetByKey = (presetKey?: PresetType) => {
    if (!presetKey) return null;

    return presets.find(preset => preset.key === presetKey);
  };

  const [filters] = useState<SimpleFilters>({});
  const [query, setQuery] = useState<ScannerProtos.Query>(getPresetByKey(props.preset)?.query || presets[0].query);
  const [result, setResult] = useState<ScannerProtos.IQueryResponse | null | undefined>(props.initialScannerResult);

  const handleFiltersChange = (filters: SimpleFilters) => {
    const path = window.location.pathname;
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([fieldName, clause]) => {
      params.append('filter', `${fieldName} ${clause}`);
    });

    const formattedParams = params.toString();

    const url = formattedParams.length ? `${path}?${formattedParams}` : path;
    window.history.pushState({}, 'Title', url);
  };

  const handlePresetClick = (query: ScannerProtos.Query, presetName: PresetType) => {
    setResult(null);
    setQuery(query);
    setActive(presetName);
    if (props.onPresetChange) {
      props.onPresetChange(query, presetName);
    }
  };

  const updatedDate = (result?: ScannerProtos.IQueryResponse | null) => {
    if (Array.isArray(result?.instruments) && result?.instruments.length) {
      const firstInstrumentDate = result.instruments[0]?.sharesShortReceiptDate;
      if (firstInstrumentDate) {
        return DateTime.fromFormat(String(firstInstrumentDate), 'yyyyMMdd').toFormat('yyyy-MM-dd');
      }
    }

    return '-';
  };

  const renderPresets = () => {
    return presets.map(preset => {
      return (
        <Preset
          active={active}
          key={preset.key}
          label={preset.label}
          name={preset.key}
          onClick={handlePresetClick}
          query={preset.query}
        />
      );
    });
  };

  const toolbar = [
    ...renderPresets(),
    <div key="spacer" style={{ flex: 1 }}>
      &nbsp;
    </div>,
    <div className="last-updated" key="date">
      <div>Updated</div>
      <div>{updatedDate(result)}</div>
    </div>,
  ];

  const footer = () => {
    return (
      <ShortInterestTableFooter>
        <span>
          Data brought to you by{' '}
          <a
            href="https://www.benzinga.com/apis/data/?utm_source=benzinga.com&utm_medium=web&utm_campaign=internal"
            rel="noreferrer"
            target="_blank"
          >
            Benzinga APIs
          </a>
        </span>
      </ShortInterestTableFooter>
    );
  };

  return (
    <ShortInterestWrapper>
      <SimpleScanner
        columns={columns}
        extraFields={['sharesShortReceiptDate']}
        filterControls={[]}
        filters={filters}
        initialData={result}
        onFiltersChange={handleFiltersChange}
        onLoad={setResult}
        query={query}
        scannerAddr="/api/scanner/short-interest"
        showPagination={true}
        tableFooter={footer()}
        tableSize={{ height: 400, width: 1200 }}
        toolbar={toolbar}
      />
      <span>
        Get Real-Time Data and start a free 14-day trial at{' '}
        <a href="https://pro.benzinga.com" rel="noreferrer noopener" target="_blank">
          Benzinga PRO
        </a>
        .
      </span>
    </ShortInterestWrapper>
  );
};

export const ShortInterestTableFooter = styled.div`
  text-align: center;
  padding: 5px 0;
  display: flex;
  flex-direction: column;
`;

export const ShortInterestWrapper = styled.div`
  min-height: 420px;
  .last-updated {
    padding: 8px 0px;
    font-size: 12px;
    min-width: 70px;
    text-align: right;
  }
  .ticker-wrapper {
    display: flex;
    align-items: center;
  }
  .buy-btn-wrapper {
    display: inline-block;
    margin-left: 6px;
    white-space: nowrap;
  }
`;
